import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

// Utilidades para WebAuthn
const bufferToBase64URL = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const base64URLToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const BiometricPromptModal = () => {
  const { API, user } = useContext(AuthContext);
  const [show, setShow] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alreadyEnabled, setAlreadyEnabled] = useState(false);

  useEffect(() => {
    checkIfShouldShow();
  }, [user]);

  const checkIfShouldShow = async () => {
    if (!user) return;

    // 1. Verificar si el dispositivo soporta biométrico (móvil o PC con Windows Hello/Touch ID)
    if (!window.PublicKeyCredential) return;
    
    try {
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!available) return;
      setIsSupported(true);

      // 2. Verificar si ya se mostró este prompt (guardado en localStorage)
      const promptShown = localStorage.getItem(`biometric_prompt_shown_${user.id}`);
      if (promptShown === 'true') return;

      // 3. Verificar si ya tiene biométrico habilitado
      const response = await axios.get(`${API}/auth/webauthn/status`);
      if (response.data.biometric_enabled) {
        setAlreadyEnabled(true);
        // Marcar como mostrado para no volver a verificar
        localStorage.setItem(`biometric_prompt_shown_${user.id}`, 'true');
        return;
      }

      // 4. Mostrar el modal después de un pequeño delay
      setTimeout(() => setShow(true), 1500);

    } catch (error) {
      console.log('Error checking biometric support:', error);
    }
  };

  const handleSkip = () => {
    // Marcar que ya se mostró para no volver a aparecer
    localStorage.setItem(`biometric_prompt_shown_${user.id}`, 'true');
    setShow(false);
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      // 1. Iniciar registro
      const startResponse = await axios.post(`${API}/auth/webauthn/register/start`);
      const options = startResponse.data.options;

      // 2. Convertir datos para WebAuthn
      options.challenge = base64URLToBuffer(options.challenge);
      options.user.id = base64URLToBuffer(options.user.id);
      
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(cred => ({
          ...cred,
          id: base64URLToBuffer(cred.id)
        }));
      }

      // 3. Crear credencial
      const credential = await navigator.credentials.create({
        publicKey: options
      });

      // 4. Preparar respuesta
      const credentialResponse = {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
          attestationObject: bufferToBase64URL(credential.response.attestationObject),
        },
        device_name: detectDeviceName()
      };

      // 5. Completar registro
      await axios.post(`${API}/auth/webauthn/register/complete`, {
        credential: credentialResponse
      });

      toast.success('🔐 ¡Acceso biométrico activado! Ahora puedes entrar más rápido.');
      
      // Marcar como mostrado
      localStorage.setItem(`biometric_prompt_shown_${user.id}`, 'true');
      setShow(false);

    } catch (error) {
      console.error('Error configurando biométrico:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Configuración cancelada. Puedes activarlo después en tu Perfil.');
        handleSkip();
      } else {
        toast.error('Error al configurar. Intenta de nuevo en Perfil → Seguridad.');
      }
    } finally {
      setLoading(false);
    }
  };

  const detectDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone (Face ID/Touch ID)';
    if (/iPad/.test(ua)) return 'iPad (Face ID/Touch ID)';
    if (/Macintosh/.test(ua)) return 'Mac (Touch ID)';
    if (/Android/.test(ua)) return 'Android (Huella/Facial)';
    if (/Windows/.test(ua)) return 'Windows Hello';
    return 'Dispositivo biométrico';
  };

  if (!show || !isSupported || alreadyEnabled) return null;

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <i className="fas fa-fingerprint text-4xl"></i>
          </div>
          <h2 className="text-xl font-bold">¿Activar acceso rápido?</h2>
        </div>

        {/* Contenido */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            Usa tu <strong>huella dactilar</strong> o <strong>reconocimiento facial</strong> para iniciar sesión más rápido. 
            Tu contraseña seguirá funcionando.
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <i className="fas fa-bolt text-yellow-500"></i>
              <span>Inicia sesión en segundos</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <i className="fas fa-shield-alt text-green-500"></i>
              <span>Más seguro que una contraseña</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <i className="fas fa-lock text-indigo-500"></i>
              <span>Tus datos nunca salen del dispositivo</span>
            </div>
          </div>

          {/* Botones */}
          <div className="space-y-3">
            <Button
              onClick={handleSetup}
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-3"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Configurando...
                </div>
              ) : (
                <>
                  <i className="fas fa-fingerprint mr-2"></i>
                  Configurar ahora
                </>
              )}
            </Button>
            
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="w-full text-gray-500 hover:text-gray-700"
            >
              Quizás después
            </Button>
          </div>

          <p className="text-xs text-gray-400 text-center mt-4">
            Puedes activarlo cuando quieras en Perfil → Seguridad
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BiometricPromptModal;
